import React from 'react';
import i18n from '../../../../i18n';
import { formatDate } from '../../../../utils/common';
import { StatusNewBadge } from '../../../common/StatusBadge';
import HeaderBanner from '../../../common/HeaderBanner';
import { SideBar } from '../../sidebar';
import FilterSection from '../../../common/Filter';
import Dropdown from '../../../common/DropDown';
import { TableSkeleton } from '../../../common/skelton/TableSkelton';
import DataTable from 'react-data-table-component';
import { tableStyles } from '../../../../styles/components/js/tableStyle';
import NoData from '../../../common/NoData';
import { NavLink } from 'react-router-dom';
import { getAuctionRequestList } from './services/auctionRequestService';
import { getLoggedUser } from '../../../../state/user';

const user = getLoggedUser();

// CHANGED: kept local instead of importing from constants/status.js — the
// import was returning undefined (nothing was actually added there yet),
// so AUCTION_REQUEST_STATUS[row.status] threw and crashed the whole page
// to a blank white screen. Move these into constants/status.js later if
// you want them shared, but keep them defined either here or there, not
// just referenced.
// StatusNewBadge expects { text, color } — not { label, color }.
const AUCTION_REQUEST_STATUS = {
  pending: { text: 'Pending', color: '#f59e0b' },
  approved: { text: 'Approved', color: '#22c55e' },
  rejected: { text: 'Rejected', color: '#ef4444' },
  synced: { text: 'Synced', color: '#3b82f6' },
};
const AUCTION_REQUEST_TYPE = {
  create: { text: 'Create', color: '#3b82f6' },
  update: { text: 'Update', color: '#f59e0b' },
  delete: { text: 'Delete', color: '#ef4444' },
};

export class AuctionRequestList extends React.Component {
  constructor() {
    super();
    this.state = {
      search: {
        status: '',
        yms_group_id: '',
        yms_auction_id: '',
        startDate: null,
        endDate: null,
      },
      requests: [],
      isloading: false,
      limit: 15,
      page: 1,
      total: 0,
      hideSidebar: false,
    };

    this.handleSearchChange = this.handleSearchChange.bind(this);
  }

  componentDidMount() {
    window.scrollTo(0, 0);

    const params = new URLSearchParams(window.location.search);
    const search = {
      status: params.get('status') || '',
      yms_group_id: params.get('yms_group_id') || '',
      yms_auction_id: params.get('yms_auction_id') || '',
      startDate: params.get('startDate') || null,
      endDate: params.get('endDate') || null,
    };
    const page = parseInt(params.get('page')) || 1;
    const limit = parseInt(params.get('limit')) || 15;

    this.setState({ search, page, limit }, () => {
      this.fetchAuctionRequests();
    });
  }

  updateUrlQuery(q) {
    const queryString = new URLSearchParams(
      Object.fromEntries(Object.entries(q).filter(([_, v]) => v !== '' && v !== null))
    ).toString();
    window.history.replaceState(null, '', `${window.location.pathname}?${queryString}`);
  }

  async fetchAuctionRequests() {
    this.setState({ isloading: true });
    const { limit, page } = this.state;
    const { status, yms_group_id, yms_auction_id, startDate, endDate } = this.state.search;

    const q = { page, limit };
    if (status) q.status = status;
    if (yms_group_id) q.yms_group_id = yms_group_id;
    if (yms_auction_id) q.yms_auction_id = yms_auction_id;
    if (startDate) q.startDate = startDate;
    if (endDate) q.endDate = endDate;

    this.updateUrlQuery(q);

    try {
      const response = await getAuctionRequestList(q);
      if (response.success) {
        this.setState({
          requests: response.data,
          total: response.meta.total,
        });
      }
    } catch (error) {
      console.error('Auction request fetch failed:', error);
    } finally {
      this.setState({ isloading: false });
    }
  }

  handleSearchChange = (e) => {
    const { name, value } = e.target;
    this.setState((prevState) => ({
      search: { ...prevState.search, [name]: value },
    }));
  };

  handleSearch = () => {
    this.setState({ page: 1 }, () => this.fetchAuctionRequests());
  };

  handleClear = () => {
    const defaultSearch = {
      status: '',
      yms_group_id: '',
      yms_auction_id: '',
      startDate: null,
      endDate: null,
    };
    this.setState({ search: defaultSearch, page: 1 }, () => this.fetchAuctionRequests());
  };

  onChangePage = (page) => {
    this.setState({ page }, () => this.fetchAuctionRequests());
  };

  onChangeRowsPerPage = (limit) => {
    this.setState({ limit, page: 1 }, () => this.fetchAuctionRequests());
  };

  getColumns = () => {
    return [
      {
        id: 1,
        name: i18n.t('yms_auction_id') || 'YMS Auction ID',
        width: '160px',
        sortable: true,
        wrap: true,
        cell: (row) => <span className="highlight-table-value">{row.yms_auction_id}</span>,
      },
      {
        id: 2,
        name: i18n.t('yms_group_id') || 'YMS Group ID',
        width: '150px',
        wrap: true,
        cell: (row) => <span>{row.yms_group_id}</span>,
      },
      {
        id: 3,
        name: i18n.t('title') || 'Title',
        width: '220px',
        wrap: true,
        cell: (row) => <span className="highlight-table-value-secondary">{row.title || '-'}</span>,
      },
      {
        id: 4,
        name: i18n.t('guarantee_amount') || 'Guarantee Amount',
        width: '150px',
        wrap: true,
        right: true,
        cell: (row) => <span>{row.guarantee_amount ?? '-'}</span>,
      },
      {
        id: 5,
        name: i18n.t('start_date') || 'Start Date',
        width: '130px',
        wrap: true,
        cell: (row) => <span>{row.start_date ? formatDate(row.start_date) : '-'}</span>,
      },
      {
        id: 6,
        name: i18n.t('end_date') || 'End Date',
        width: '130px',
        wrap: true,
        cell: (row) => <span>{row.end_date ? formatDate(row.end_date) : '-'}</span>,
      },
      {
        id: 7,
        name: i18n.t('request_type') || 'Request Type',
        width: '120px',
        wrap: true,
        cell: (row) => <StatusNewBadge status={AUCTION_REQUEST_TYPE[row.request_type]} />,
      },
      {
        id: 8,
        name: i18n.t('status') || 'Status',
        width: '120px',
        wrap: true,
        cell: (row) => <StatusNewBadge status={AUCTION_REQUEST_STATUS[row.status]} />,
      },
      {
        id: 9,
        name: i18n.t('submitted_on') || 'Submitted On',
        width: '140px',
        wrap: true,
        cell: (row) => <span>{row.created_at ? formatDate(row.created_at) : '-'}</span>,
      },
      {
        id: 10,
        name: i18n.t('action') || 'Action',
        width: '100px',
        center: true,
        cell: (row) => (
          <NavLink
            to={`/auction-request/${row.public_id}`}
            className="button1 button1-outline button1-action"
          >
            {i18n.t('view') || 'View'}
          </NavLink>
        ),
      },
    ];
  };

  render() {
    const { requests, isloading, hideSidebar } = this.state;

    return (
      <React.Fragment>
        <HeaderBanner
          title={i18n.t('auction_requests') || 'Auction Requests'}
          subtitle={
            i18n.t('review_auction_requests_from_yms') ||
            'Review pending, approved, rejected and synced auction requests submitted by YMS'
          }
        />
        <section className="dashboard-section">
          <div className="container">
            <div className="row">
              <div className="col-xl-3 col-lg-12 col-md-12">{!hideSidebar && <SideBar />}</div>
              <div
                className={
                  hideSidebar ? 'col-xl-12 col-lg-12 col-md-12' : 'col-xl-9 col-lg-12 col-md-12'
                }
              >
                <div className="card">
                  <div className="card-header">
                    <div className="expand-dashboard">
                      <span
                        style={{ fontSize: '1rem', marginRight: '0.5rem', cursor: 'pointer' }}
                        onClick={() => this.setState({ hideSidebar: !hideSidebar })}
                      >
                        {hideSidebar && <i className="icon-size-actual"></i>}
                        {!hideSidebar && <i className="icon-size-fullscreen"></i>}
                      </span>
                    </div>
                  </div>
                  <div className="card-body">
                    <FilterSection
                      loading={isloading}
                      onSearch={this.handleSearch}
                      onClear={this.handleClear}
                    >
                      <Dropdown
                        name="status"
                        value={this.state.search.status}
                        onChange={this.handleSearchChange}
                        placeholder={i18n.t('status') || 'Status'}
                        empty={i18n.t('select_status') || 'Select status'}
                        options={[
                          { value: 'pending', label: i18n.t('pending') || 'Pending' },
                          { value: 'approved', label: i18n.t('approved') || 'Approved' },
                          { value: 'rejected', label: i18n.t('rejected') || 'Rejected' },
                          { value: 'synced', label: i18n.t('synced') || 'Synced' },
                        ]}
                      />

                      <div className="select-wrapper floating-label-wrapper">
                        <input
                          id="yms_group_id"
                          type="text"
                          name="yms_group_id"
                          value={this.state.search.yms_group_id || ''}
                          onChange={this.handleSearchChange}
                          className="filter-base floating-input"
                          placeholder=" "
                        />
                        <label htmlFor="yms_group_id" className="floating-label">
                          {i18n.t('yms_group_id') || 'YMS Group ID'}
                        </label>
                      </div>

                      <div className="select-wrapper floating-label-wrapper">
                        <input
                          id="yms_auction_id"
                          type="text"
                          name="yms_auction_id"
                          value={this.state.search.yms_auction_id || ''}
                          onChange={this.handleSearchChange}
                          className="filter-base floating-input"
                          placeholder=" "
                        />
                        <label htmlFor="yms_auction_id" className="floating-label">
                          {i18n.t('yms_auction_id') || 'YMS Auction ID'}
                        </label>
                      </div>

                      <div className="select-wrapper floating-label-wrapper">
                        <input
                          id="startDate"
                          type="date"
                          name="startDate"
                          value={this.state.search.startDate || ''}
                          onChange={this.handleSearchChange}
                          className="filter-base floating-input"
                          placeholder=" "
                        />
                        <label htmlFor="startDate" className="floating-label">
                          {i18n.t('start_date') || 'Start Date'}
                        </label>
                      </div>

                      <div className="select-wrapper floating-label-wrapper">
                        <input
                          id="endDate"
                          type="date"
                          name="endDate"
                          value={this.state.search.endDate || ''}
                          onChange={this.handleSearchChange}
                          className="filter-base floating-input"
                          placeholder=" "
                        />
                        <label htmlFor="endDate" className="floating-label">
                          {i18n.t('end_date') || 'End Date'}
                        </label>
                      </div>
                    </FilterSection>

                    <div className="Data-Listing">
                      {isloading ? (
                        <TableSkeleton columns={10} rows={10} />
                      ) : requests.length ? (
                        <DataTable
                          responsive={true}
                          columns={this.getColumns()}
                          data={requests}
                          customStyles={tableStyles}
                          highlightOnHover
                          pagination
                          paginationServer
                          paginationTotalRows={this.state.total}
                          paginationPerPage={this.state.limit}
                          paginationDefaultPage={this.state.page}
                          paginationRowsPerPageOptions={[15, 25, 50, 100]}
                          paginationComponentOptions={{
                            rowsPerPageText: i18n.t('rows_per_page'),
                            rangeSeparatorText: i18n.t('out'),
                          }}
                          onChangePage={this.onChangePage}
                          onChangeRowsPerPage={this.onChangeRowsPerPage}
                        />
                      ) : (
                        <NoData
                          message={
                            i18n.t('no_auction_requests_available') || 'No auction requests found'
                          }
                          actionText={i18n.t('refresh')}
                          onAction={this.fetchAuctionRequests.bind(this)}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </React.Fragment>
    );
  }
}
